import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner } from "reactstrap";
import { renderControl } from "../../Empresa/Parametros/renderControl";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import { useAuth } from "../../../hooks/auth";
import ChatModal from "../../../components/autopilot/ChatModal";
import autopilotMiniIcon from "../../../assets/img/autopilot-mini-icon.png";
import { contextoAjusteEmail, perguntaVisivel, textoSemPendencias } from "./mensagemAutopilotIntegracao";
import {
  getIntegracao,
  salvarIntegracao,
  testarConexao,
  listarServicosConector,
  montarEmailAutopilot,
} from "../../../services/integracaoTransportadoraApi";
import "../../Empresa/Parametros/Parametros.css";

// Aba "Configurar Integração" (H5, PO1-2550): form dinâmico do conector vigente
// da transportadora + toggles de capacidade + Testar conexão. Reusa renderControl.
// Campo com tipo "multiselect-servicos" vira uma lista de checkboxes alimentada
// pelos serviços contratados do conector (ex.: Correios via meucontrato); se a
// lista não carregar, cai no campo texto (PO1-2554).
// PO1-3484: botão do Autopilot que monta o e-mail pedindo à transportadora os dados
// que faltam (o backend usa estes mesmos campos, então vale para qualquer conector) e
// abre o chat do Autopilot com a resposta já pronta.
const CAP_PREFIX = "cap_";
const TIPO_MULTISELECT_SERVICOS = "multiselect-servicos";

const parseCodigos = (valor) =>
  String(valor || "")
    .split(/[\s,;]+/)
    .filter(Boolean);

const autopilotButtonStyle = {
  background: AUTOPILOT_COLORS.purpleBg,
  border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`,
  borderRadius: 4,
  color: AUTOPILOT_COLORS.purpleText,
  fontSize: "0.75rem",
  padding: "4px 10px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
};

export default function IntegracaoTab({ transportadoraId, embarcadorId, onChanged, transportadora }) {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [chat, setChat] = useState(null);
  const [montandoEmail, setMontandoEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [info, setInfo] = useState(null);
  const [valores, setValores] = useState({});
  // Opções do multiselect de serviços: null=não precisa, {loading}, {servicos,...}
  const [servicosInfo, setServicosInfo] = useState(null);

  const carregar = useCallback(async () => {
    if (!transportadoraId) return;
    setLoading(true);
    try {
      const data = await getIntegracao(transportadoraId, embarcadorId);
      setInfo(data);
      const init = {};
      (data.campos || []).forEach((c) => {
        if (c.valor !== null && c.valor !== undefined) init[c.idParametro] = String(c.valor);
        else if (c.tipo === "toggle") init[c.idParametro] = "Nao";
        else init[c.idParametro] = "";
      });
      setValores(init);
    } catch (e) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: e.response?.data?.error || "Falha ao carregar a integração.",
      });
      setInfo({ conector: null, campos: [] });
    } finally {
      setLoading(false);
    }
  }, [transportadoraId, embarcadorId, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Carrega as opções do multiselect de serviços quando algum campo usa esse tipo.
  useEffect(() => {
    const precisa = (info?.campos || []).some((c) => c.tipo === TIPO_MULTISELECT_SERVICOS);
    if (!precisa || !embarcadorId) {
      setServicosInfo(null);
      return;
    }
    let ativo = true;
    setServicosInfo({ loading: true });
    listarServicosConector(transportadoraId, embarcadorId)
      .then((r) => {
        if (ativo) setServicosInfo(r || { suportado: false, servicos: [] });
      })
      .catch(() => {
        if (ativo) setServicosInfo({ suportado: false, servicos: [] });
      });
    return () => {
      ativo = false;
    };
  }, [info, transportadoraId, embarcadorId]);

  const onChange = (idParametro, val) =>
    setValores((prev) => ({ ...prev, [idParametro]: val }));

  const toggleServico = (idParametro, codigo) => {
    const atuais = parseCodigos(valores[idParametro]);
    const novos = atuais.includes(codigo)
      ? atuais.filter((c) => c !== codigo)
      : atuais.concat(codigo);
    onChange(idParametro, novos.join(","));
  };

  const salvar = async () => {
    setSaving(true);
    try {
      const payload = (info.campos || []).map((c) => ({
        idParametro: c.idParametro,
        valor: valores[c.idParametro],
      }));
      await salvarIntegracao(transportadoraId, { embarcadorId, valores: payload });
      addToast({ place: "tr", type: "success", title: "Salvo", description: "Integração salva." });
      if (onChanged) onChanged();
      carregar();
    } catch (e) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: e.response?.data?.error || "Falha ao salvar a integração.",
      });
    } finally {
      setSaving(false);
    }
  };

  const testar = async () => {
    setTesting(true);
    try {
      const r = await testarConexao(transportadoraId, { embarcadorId });
      addToast({
        place: "tr",
        type: r.ok ? "success" : "warning",
        title: r.ok ? "Conexão OK" : "Atenção",
        description: r.mensagem || (r.ok ? "Conexão validada." : "Não foi possível validar."),
      });
    } catch (e) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: e.response?.data?.error || "Falha ao testar a conexão.",
      });
    } finally {
      setTesting(false);
    }
  };

  const noticeStyle = { color: AUTOPILOT_COLORS.muted, padding: "1rem", fontSize: "0.85rem" };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Spinner color="info" />
      </div>
    );
  }
  if (!info || !info.conector) {
    return (
      <div style={noticeStyle}>Esta transportadora não tem um conector de cotação configurado.</div>
    );
  }
  if (!embarcadorId) {
    return <div style={noticeStyle}>Selecione um embarcador para configurar a integração.</div>;
  }

  const credenciais = (info.campos || []).filter((c) => !String(c.nome).startsWith(CAP_PREFIX));

  const abrirAutopilot = async () => {
    setMontandoEmail(true);
    try {
      const r = await montarEmailAutopilot(transportadoraId, {
        embarcadorId,
        remetenteNome: user ? [user.name, user.surname].filter(Boolean).join(" ") : null,
      });
      setChat({
        title: `E-mail para ${(transportadora && transportadora.nome) || "a transportadora"}`,
        pergunta: perguntaVisivel(transportadora),
        resposta: r.semPendencias ? textoSemPendencias(r.paraVoce) : r.texto,
        contexto: r.semPendencias ? null : contextoAjusteEmail(transportadora, r.texto),
      });
    } catch (e) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Autopilot",
        description: e.response?.data?.error || "Não foi possível montar o e-mail agora.",
      });
    } finally {
      setMontandoEmail(false);
    }
  };
  const capacidades = (info.campos || []).filter((c) => String(c.nome).startsWith(CAP_PREFIX));

  const paramFor = (c) => ({
    Id: c.idParametro,
    Tipo: c.tipo,
    Opcoes: c.opcoes,
    Sufixo: c.sufixo,
    Min: c.min,
    Max: c.max,
    Placeholder:
      c.tipo === "password" && c.hasValue
        ? "•••••• (salvo — deixe em branco para manter)"
        : c.placeholder,
  });

  const labelStyle = {
    color: AUTOPILOT_COLORS.muted,
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "0.25rem",
  };
  const rowStyle = { marginBottom: "0.9rem" };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
          flexWrap: "wrap",
          fontSize: "0.8rem",
          color: AUTOPILOT_COLORS.muted,
          marginBottom: "1rem",
        }}
      >
        <span>
          Conector:{" "}
          <strong style={{ color: AUTOPILOT_COLORS.text }}>
            {info.conector.key}
            {info.conector.versao ? ` ${info.conector.versao}` : ""}
          </strong>
        </span>
        {credenciais.length > 0 && (
          <button
            type="button"
            onClick={abrirAutopilot}
            disabled={montandoEmail}
            style={{ ...autopilotButtonStyle, opacity: montandoEmail ? 0.7 : 1, cursor: montandoEmail ? "default" : "pointer" }}
            title="O Autopilot monta o e-mail com o que pedir à transportadora"
          >
            {montandoEmail ? (
              <Spinner size="sm" style={{ width: 12, height: 12 }} />
            ) : (
              <img src={autopilotMiniIcon} alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
            )}
            {montandoEmail ? "Montando o e-mail..." : "Montar e-mail para a transportadora"}
          </button>
        )}
      </div>

      <ChatModal
        isOpen={Boolean(chat)}
        onClose={() => setChat(null)}
        preloadedUserMessage={chat ? chat.pergunta : null}
        preloadedAnswer={chat ? chat.resposta : null}
        contextoInicial={chat ? chat.contexto : null}
        title={chat ? chat.title : null}
        empresaId={embarcadorId}
      />

      {credenciais.map((c) => {
        const ehMultiselect = c.tipo === TIPO_MULTISELECT_SERVICOS;
        const opcoes = (servicosInfo && servicosInfo.servicos) || [];
        const selecionados = parseCodigos(valores[c.idParametro]);

        if (ehMultiselect && servicosInfo?.loading) {
          return (
            <div key={c.idParametro} style={rowStyle}>
              <div style={labelStyle}>{c.descricao || c.nome}</div>
              <Spinner size="sm" color="info" />{" "}
              <span style={{ fontSize: "0.8rem", color: AUTOPILOT_COLORS.muted }}>
                Buscando os serviços do contrato...
              </span>
            </div>
          );
        }

        if (ehMultiselect && opcoes.length > 0) {
          return (
            <div key={c.idParametro} style={rowStyle}>
              <div style={labelStyle}>
                {c.descricao || c.nome}
                <span style={{ marginLeft: 8, textTransform: "none", letterSpacing: 0 }}>
                  ({selecionados.length} selecionado{selecionados.length === 1 ? "" : "s"})
                </span>
              </div>
              <div
                style={{
                  border: `1px solid ${AUTOPILOT_COLORS.border}`,
                  borderRadius: 6,
                  padding: "0.5rem 0.75rem",
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {opcoes.map((s) => (
                  <label
                    key={s.codigo}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      margin: "0.2rem 0",
                      cursor: saving ? "default" : "pointer",
                      color: AUTOPILOT_COLORS.text,
                      fontSize: "0.85rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selecionados.includes(s.codigo)}
                      disabled={saving}
                      onChange={() => toggleServico(c.idParametro, s.codigo)}
                      style={{ margin: 0 }}
                    />
                    <span>
                      {s.descricao}{" "}
                      <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.75rem" }}>
                        ({s.codigo})
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        }

        // Fallback: lista indisponível (credencial incompleta, conector sem
        // suporte, hub fora) -> campo texto editável, como antes.
        return (
          <div key={c.idParametro} style={rowStyle}>
            <div style={labelStyle}>{c.descricao || c.nome}</div>
            {renderControl({
              param: paramFor(ehMultiselect ? { ...c, tipo: "text" } : c),
              valor: valores[c.idParametro],
              onChange: (v) => onChange(c.idParametro, v),
              saving,
              fullWidth: true,
            })}
            {ehMultiselect && servicosInfo && servicosInfo.mensagem && (
              <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted, marginTop: 4 }}>
                {servicosInfo.mensagem}
              </div>
            )}
          </div>
        );
      })}

      {capacidades.length > 0 && (
        <div
          style={{
            marginTop: "1rem",
            borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
            paddingTop: "0.9rem",
          }}
        >
          <div style={{ ...labelStyle, marginBottom: "0.5rem" }}>Capacidades</div>
          {capacidades.map((c) => (
            <div
              key={c.idParametro}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                ...rowStyle,
              }}
            >
              <span style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.85rem" }}>
                {c.descricao || c.nome}
              </span>
              {renderControl({
                param: paramFor(c),
                valor: valores[c.idParametro],
                onChange: (v) => onChange(c.idParametro, v),
                saving,
              })}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.25rem" }}>
        <Button color="success" size="sm" onClick={salvar} disabled={saving}>
          {saving ? <Spinner size="sm" /> : "Salvar"}
        </Button>
        <Button color="info" size="sm" outline onClick={testar} disabled={testing || saving}>
          {testing ? <Spinner size="sm" /> : "Testar conexão"}
        </Button>
      </div>
    </div>
  );
}
